import { GridEnrichedColDef, GridRowParams, GridActionsCellItem } from '@mui/x-data-grid-pro';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useStore } from '../../../../hooks/useStore';
import { Chip, ContentHead } from '../../../../lib/components';
import { useDialog } from '../../../../lib/components/compound/dialogs/confirmationDialog/useDialog';
import { ContentBox } from '../../../../lib/components/compound/pages/contentBox';
import { EditIcon, DeleteIcon } from '../../../../lib/icons';
import { AddButton } from '../shared/addButton';
import { CompanyTable } from '../shared/companyTable';
import { ConfirmDeleteDialog } from '../shared/confirmDelete';

export const CompanyDepartmentsData = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyStore } = useStore();
  const dialog = useDialog<string>();

  const departments =
    companyStore.departments?.map((department) => ({
      id: department.id,
      name: department.name,
      active: department.active
    })) || [];

  const columns: Array<GridEnrichedColDef> = [
    {
      field: 'name',
      headerName: t('company:DEPARTMENT'),
      flex: 3
    },
    {
      field: 'active',
      headerName: t('common:STATUS'),
      flex: 2,
      type: 'boolean',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Chip
          label={params.value ? t('employee:ACTIVE') : t('employee:INACTIVE')}
          color={params.value ? 'default' : 'secondary'}
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={`${params.id}-edit`}
          icon={<EditIcon />}
          showInMenu
          label={t('common:EDIT')}
          onClick={() => navigate(`./${params.id}`)}
          placeholder=""
        />,
        <GridActionsCellItem
          key={`${params.id}-delete`}
          icon={<DeleteIcon />}
          showInMenu
          label={t('common:DELETE')}
          onClick={() => dialog.setDialogRelevantValue(params.id as string)}
          placeholder=""
        />
      ]
    }
  ];

  useEffect(() => {
    if (!companyStore.company) return;
    const abortController = new AbortController();

    const fetchDepartments = async () => {
      try {
        await companyStore.fetchDepartments();
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(error);
        }
      }
    };
    fetchDepartments();

    return () => abortController.abort();
  }, [companyStore.company]);

  return (
    <ContentBox
      headComponent={
        <ContentHead
          headline={t('company:DEPARTMENTS', { count: companyStore.departments?.length })}
          showActions={true}
          renderActions={() => <AddButton onClick={() => navigate('new')} />}
        />
      }
    >
      <CompanyTable
        rows={departments}
        columns={columns}
        onRowDoubleClick={(row) => {
          navigate(`${row.id}`);
        }}
      />
      <ConfirmDeleteDialog
        type="department"
        open={dialog.dialogProps.open}
        onClose={dialog.dialogProps.onClose}
        onConfirm={() =>
          dialog.dialogRelevantValue
            ? companyStore.deleteDepartment(dialog?.dialogRelevantValue)
            : console.error('No department id provided')
        }
      />
    </ContentBox>
  );
});
